import React, { useMemo } from "react";
import { Box, HStack, Icon, Text } from "@chakra-ui/react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import { DataFrameAnalysisProto } from "build-ts/dataframe";
import type { VegaView } from "vega-embed";
import { getArtifactType } from "modules/artifacts/utils/artifactUtils";
import { getParentPillIcon } from "../utils/dataFrameCodeUtils";
import { buildKGColumnTooltips } from "./KGColumnTooltips";
import KGDataFrameFilters from "./KGDataFrameFilters";
import PandasTable from "./PandasTable";
import { AnalysisChartTab } from "./DataFrameAnalysis";

/**
 * Shows the parent input tables/charts for a DataFrameAnalysis, mirroring the Results tab layout.
 * Returns a Fragment so the sticky pills row and content are direct children of the
 * parent overflowY="auto" scroll container in DataFrameAnalysis — same as the Results tab.
 */
export const InputsTab: React.FC<{
  data: DataFrameAnalysisProto;
  lookupFn: (name: string) => ArtifactProto | undefined;
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  isPopulatingInput: boolean;
  onChartViewReady?: (view: VegaView) => void;
}> = ({ data, lookupFn, selectedIndex, onSelectedIndexChange, isPopulatingInput, onChartViewReady }) => {
  const parents = data.parent_headers ?? [];
  const selectedParent = parents[Math.min(selectedIndex, parents.length - 1)];
  const artifact = selectedParent ? lookupFn(selectedParent.name ?? "") : undefined;
  const artifactType = artifact ? getArtifactType(artifact) : null;

  // Resolve PandasDataFrameProto from whichever artifact type holds this parent.
  // DATAFRAME_ANALYSIS: pandas_df lives as a sub-resource — find by name.
  // KG_DATAFRAME:       pandas_df is the inline .pandas_dataframe field.
  // FILE_ARTIFACT:      pandas_df is the inline .pandas_dataframe field.
  const pandasDf = useMemo(() => {
    if (artifactType === ArtifactType.DATAFRAME_ANALYSIS) {
      return (artifact?.dataframe_analysis?.pandas_dataframes ?? []).find(d => d.name === selectedParent?.name) ?? null;
    }
    if (artifactType === ArtifactType.KG_DATAFRAME) {
      return artifact?.kg_dataframe?.pandas_dataframe ?? null;
    }
    if (artifactType === ArtifactType.FILE_ARTIFACT) {
      return artifact?.file_artifact?.pandas_dataframe ?? null;
    }
    return null;
  }, [artifact, artifactType, selectedParent?.name]);

  // Resolve a chart spec when the parent is a chart sub-resource within a DATAFRAME_ANALYSIS.
  const chartSpecJson = useMemo(() => {
    if (artifactType !== ArtifactType.DATAFRAME_ANALYSIS) return null;
    const chart = (artifact?.dataframe_analysis?.charts ?? []).find(c => c.name === selectedParent?.name);
    return chart?.spec_json ?? null;
  }, [artifact, artifactType, selectedParent?.name]);

  // Build MKG entity tooltips for KG_DATAFRAME column headers.
  const columnTooltips = useMemo(
    () => (artifact?.kg_dataframe ? buildKGColumnTooltips(artifact.kg_dataframe) : {}),
    [artifact]
  );

  if (parents.length === 0) {
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          No input tables.
        </Text>
      </Box>
    );
  }

  return (
    <>
      {/* ── Pills row — sticky against the parent overflowY="auto" scroll container ── */}
      <HStack
        spacing={1.5}
        px={2}
        py={1.5}
        borderBottom="1px solid"
        borderColor="gray.100"
        bg="white"
        flexWrap="wrap"
        position="sticky"
        top={0}
        zIndex={2}
        boxShadow="sm"
      >
        {parents.map((ph, i) => {
          const isActive = i === selectedIndex;
          return (
            <Box
              key={ph.name ?? i}
              as="button"
              type="button"
              display="inline-flex"
              alignItems="center"
              px={3}
              py={1}
              borderRadius="md"
              border={isActive ? "2px solid" : "1px solid"}
              borderColor={isActive ? "purple.600" : "gray.300"}
              bg="white"
              color={isActive ? "purple.700" : "gray.600"}
              fontSize="xs"
              fontWeight={isActive ? "700" : "600"}
              _hover={{
                borderColor: "purple.400",
                color: isActive ? "purple.700" : "purple.600",
                bg: "purple.50",
              }}
              transition="all 0.15s"
              cursor="pointer"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onSelectedIndexChange(i);
              }}
            >
              <HStack as="span" spacing={1.5} align="center">
                <Icon
                  as={getParentPillIcon(ph.name, lookupFn)}
                  boxSize={3}
                  color="currentColor"
                  flexShrink={0}
                  aria-hidden
                />
                <Text as="span" fontSize="xs" lineHeight="1.2" mb={0} isTruncated>
                  {ph.name ?? `Input ${i + 1}`}
                </Text>
              </HStack>
            </Box>
          );
        })}
      </HStack>

      {/* ── Filters (read-only, KG_DATAFRAME parents only) ── */}
      {artifactType === ArtifactType.KG_DATAFRAME && artifact?.kg_dataframe && !isPopulatingInput && (
        <KGDataFrameFilters
          data={artifact.kg_dataframe}
          checkedExpressionFilters={{}}
          isRecomputing={false}
          hasExpressionFiltersChanged={false}
          onExpressionFilterToggle={() => {}}
          onRecompute={() => {}}
          readOnly
        />
      )}

      {/* ── Content area ─────────────────────────────────────────────────────────
        Blank while the parent artifact is being fetched (existing green loader
        handles the feedback). Once data arrives, renders either:
        - A vega chart when the selected parent is a chart sub-resource.
        - A PandasTable when the selected parent is a dataframe.
        - A "not available" placeholder when the dataframe payload is missing.
      */}
      {(() => {
        // Stay blank while the parent artifact is loading.
        if (isPopulatingInput) return null;

        // Selected parent resolved to a chart sub-resource inside a DATAFRAME_ANALYSIS.
        if (chartSpecJson) {
          return (
            <AnalysisChartTab
              key={selectedParent?.name}
              chartVegaSpecJson={chartSpecJson}
              onViewReady={onChartViewReady}
            />
          );
        }

        // Selected parent is a dataframe (KG_DATAFRAME or pandas_df sub-resource).
        if (!chartSpecJson) {
          if (pandasDf) {
            return <PandasTable df={pandasDf} columnTooltips={columnTooltips} />;
          }
          // Data arrived but the specific dataframe payload is absent.
          return (
            <Box px={4} py={6} textAlign="center">
              <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
                Table data not available.
              </Text>
            </Box>
          );
        }

        return null;
      })()}
    </>
  );
};
